import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import profile from '../statics/images/profile.png'
import { useAuth } from '../context/AuthProvider';
import { ThemeContext } from './ThemeProvider';
import {
    useNotifications,
    getNotificationLineId,
    getNotificationOrderNumber,
    getNotificationQuoteId,
    getNotificationPoLineId,
} from '../context/NotificationsContext';
import { extractCommentText } from '../utils/commentText';

const AVATAR_COLORS = ['#7c3aed', '#16a34a', '#ea580c', '#be185d', '#0f766e', '#2563eb'];

const personName = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'Someone';
    const base = text.includes('@') ? text.split('@')[0] : text;
    return base
        .split(/[._\s-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const initialsFor = (name) => {
    const parts = String(name || '').split(' ').filter(Boolean);
    const letters = `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`;
    return (letters || String(name || 'NA').slice(0, 2)).toUpperCase();
};

const avatarColor = (name) => {
    const text = String(name || '');
    const sum = [...text].reduce((total, char) => total + char.charCodeAt(0), 0);
    return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const timeOf = (notification) => {
    const time = new Date(notification?.createdAt || 0).getTime();
    return Number.isFinite(time) ? time : 0;
};

const isCurrentUser = (notification, me) => {
    const by = String(notification?.mentionedBy || '').trim().toLowerCase();
    if (!by || !me) return false;
    return by === me || by.split('@')[0] === me.split('@')[0];
};

const activityBucket = (createdAt) => {
    const when = new Date(createdAt || 0);
    if (!Number.isFinite(when.getTime())) return 'EARLIER';
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const day = new Date(when);
    day.setHours(0, 0, 0, 0);
    const diff = Math.round((start.getTime() - day.getTime()) / 86400000);
    if (diff <= 0) return 'TODAY';
    if (diff === 1) return 'YESTERDAY';
    if (diff < 7) return 'EARLIER THIS WEEK';
    return 'EARLIER';
};

const formatWhen = (createdAt) => {
    const when = new Date(createdAt || 0);
    if (!Number.isFinite(when.getTime())) return '';
    const bucket = activityBucket(createdAt);
    if (bucket === 'TODAY' || bucket === 'YESTERDAY') {
        return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return when.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const buildActivity = (notifications, me) => {
    const list = Array.isArray(notifications) ? notifications : [];
    const used = new Set();
    const cards = [];
    list.forEach((notification) => {
        if (isCurrentUser(notification, me)) return;
        const orderNumber = getNotificationOrderNumber(notification);
        const writtenAt = timeOf(notification);
        const reply = orderNumber ? list.find((other) => {
            if (used.has(other) || !isCurrentUser(other, me)) return false;
            return getNotificationOrderNumber(other) === orderNumber && timeOf(other) >= writtenAt;
        }) : null;
        if (reply) used.add(reply);
        cards.push({ notification, reply: reply || null });
    });
    list.forEach((notification) => {
        if (!isCurrentUser(notification, me) || used.has(notification)) return;
        cards.push({ notification, reply: null });
    });
    cards.sort((a, b) => timeOf(b.notification) - timeOf(a.notification));
    return cards;
};

const CommentBody = ({ text }) => {
    const parts = String(text || '').split(/(@[A-Za-z0-9][A-Za-z0-9._-]*)/g);
    return parts.map((part, index) => (
        part.startsWith('@')
            ? <strong key={`${part}-${index}`} className="gf-activity-mention">{part}</strong>
            : <span key={`${part}-${index}`}>{part}</span>
    ));
};

const SunIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
);

const MoonIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 14.5A8.5 8.5 0 1 1 9.5 3 7 7 0 0 0 21 14.5z" />
    </svg>
);

const TopBar = () => {
    const { logOut } = useAuth();
    const { theme, setThemeMode } = useContext(ThemeContext);
    const [themeOpen, setThemeOpen] = useState(false);
    const [themeMenuPos, setThemeMenuPos] = useState(null);
    const themeButtonRef = useRef(null);
    // Notifications live in NotificationsProvider so the bell, the minimized comment
    // chats at the bottom of the page and the Orders comment icons share one count.
    const {
        notifications,
        unreadCount,
        openOrderChat,
    } = useNotifications();
    const [showDropdown, setShowDropdown] = useState(false);
    const [activityTab, setActivityTab] = useState('all');
    const me = (localStorage.getItem('userEmail') || '').trim().toLowerCase();
    const activity = buildActivity(notifications, me).filter((card) => {
        if (activityTab !== 'mentions') return true;
        const comment = extractCommentText(card.notification.comment);
        return card.notification.isMention || /@/.test(comment);
    });
    const activityGroups = activity.reduce((groups, card) => {
        const label = activityBucket(card.notification.createdAt);
        const group = groups.find((item) => item.label === label);
        if (group) group.items.push(card);
        else groups.push({ label, items: [card] });
        return groups;
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.gf-notification-dock')) {
                setShowDropdown(false);
            }
            if (themeOpen && !event.target.closest('.gf-theme-switch') && !event.target.closest('.gf-theme-menu')) {
                setThemeOpen(false);
            }
        };

        if (showDropdown || themeOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, themeOpen]);

    useEffect(() => {
        if (!themeOpen) return undefined;
        const placeThemeMenu = () => {
            const rect = themeButtonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const width = 200;
            const left = Math.min(
                Math.max(12, rect.right - width),
                window.innerWidth - width - 12
            );
            setThemeMenuPos({ top: rect.bottom + 8, left });
        };
        placeThemeMenu();
        window.addEventListener('resize', placeThemeMenu);
        window.addEventListener('scroll', placeThemeMenu, true);
        return () => {
            window.removeEventListener('resize', placeThemeMenu);
            window.removeEventListener('scroll', placeThemeMenu, true);
        };
    }, [themeOpen]);

    const handleNotificationClick = () => {
        setShowDropdown(!showDropdown);
    };

    const openChat = (notification) => {
        const poLineId = getNotificationPoLineId(notification);
        openOrderChat?.({
            orderNumber: getNotificationOrderNumber(notification),
            orderLineId: poLineId ? undefined : getNotificationLineId(notification),
            quoteId: getNotificationQuoteId(notification),
            poLineId,
        });
        setShowDropdown(false);
    };

    const viewOrder = (notification) => {
        const orderNumber = getNotificationOrderNumber(notification);
        if (!orderNumber) return;
        window.open(`https://bridge.gigaflightinc.com/order-details/${orderNumber}`, '_blank');
    };

    return (
        <>
            <nav
                class="navbar navbar-expand navbar-transparent bg-transparent px-4 gf-topbar-sticky-layer"
                style={{ position: 'relative', zIndex: 5000 }}
            >
                {/* <a class="sidebar-toggle js-sidebar-toggle">
                    <i class="hamburger align-self-center"></i>
                </a> */}

                <div class="navbar-collapse collapse">
                    <ul class="navbar-nav navbar-align">
                        {createPortal(
                        <div className="gf-notification-dock">
                            <div className="gf-notification-dock-anchor">
                                <a
                                    className="nav-icon nav-icon-notification-bell d-inline-flex align-items-center justify-content-center"
                                    style={{ cursor: 'pointer', border: 'none' }}
                                    onClick={handleNotificationClick}
                                    aria-label="Open notifications"
                                >
                                    {/*
                                      Global .nav-icon svg { width/height: 20px } in style.css shrinks all nav icons.
                                      This wrapper + class + inline sizes restore a readable bell; badge sits on the corner only.
                                    */}
                                    <span
                                        className="position-relative d-inline-block"
                                        style={{ lineHeight: 0 }}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 640 640"
                                            fill="currentColor"
                                            aria-hidden
                                        >
                                            <path d="M320 64C302.3 64 288 78.3 288 96L288 99.2C215 114 160 178.6 160 256L160 277.7C160 325.8 143.6 372.5 113.6 410.1L103.8 422.3C98.7 428.6 96 436.4 96 444.5C96 464.1 111.9 480 131.5 480L508.4 480C528 480 543.9 464.1 543.9 444.5C543.9 436.4 541.2 428.6 536.1 422.3L526.3 410.1C496.4 372.5 480 325.8 480 277.7L480 256C480 178.6 425 114 352 99.2L352 96C352 78.3 337.7 64 320 64zM258 528C265.1 555.6 290.2 576 320 576C349.8 576 374.9 555.6 382 528L258 528z"></path>
                                        </svg>
                                        {unreadCount > 0 && (
                                            <span
                                                className="position-absolute rounded-pill text-white fw-bold border border-white"
                                                style={{
                                                    top: '-4px',
                                                    right: '-8px',
                                                    minWidth: unreadCount > 9 ? '22px' : '18px',
                                                    height: '18px',
                                                    fontSize: unreadCount > 99 ? '0.6rem' : '0.68rem',
                                                    padding: '0 4px',
                                                    backgroundColor: '#0d6efd',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    lineHeight: 1
                                                }}
                                            >
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                                <span className="visually-hidden">unread notifications</span>
                                            </span>
                                        )}
                                    </span>
                                </a>

                                {/* Notification Dropdown */}
                                {showDropdown && (
                                    <div className="dropdown-menu show gf-notification-menu">
                                        <div className="gf-activity-top">
                                            <div className="gf-activity-head">
                                                <div className="gf-activity-title">
                                                    <h6>Comment activity</h6>
                                                    {unreadCount > 0 && (
                                                        <span className="gf-activity-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    className="gf-activity-close"
                                                    aria-label="Close notifications"
                                                    onClick={() => setShowDropdown(false)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <div className="gf-activity-tabs">
                                                <button
                                                    type="button"
                                                    className={activityTab === 'all' ? 'is-active' : ''}
                                                    onClick={() => setActivityTab('all')}
                                                >
                                                    All comments
                                                </button>
                                                <button
                                                    type="button"
                                                    className={activityTab === 'mentions' ? 'is-active' : ''}
                                                    onClick={() => setActivityTab('mentions')}
                                                >
                                                    @ Mentions
                                                </button>
                                            </div>
                                        </div>
                                        {activityGroups.length === 0 ? (
                                            <div className="gf-activity-empty">
                                                {activityTab === 'mentions' ? 'No mentions yet' : 'No comments yet'}
                                            </div>
                                        ) : (
                                            <div className="gf-activity-list">
                                                {activityGroups.map((group) => (
                                                    <section key={group.label}>
                                                        <div className="gf-activity-section">{group.label}</div>
                                                        {group.items.map(({ notification, reply }, index) => {
                                                            const name = personName(notification.mentionedBy);
                                                            const mention = Boolean(notification.isMention);
                                                            const orderNumber = getNotificationOrderNumber(notification);
                                                            const customer = notification.customerName || notification.customer_name || '';
                                                            const comment = extractCommentText(notification.comment) || 'No comment';
                                                            const replyText = reply ? extractCommentText(reply.comment) : '';
                                                            return (
                                                                <article
                                                                    key={notification.notificationId || `${group.label}-${index}`}
                                                                    className="gf-activity-card"
                                                                >
                                                                    <div className="gf-activity-card-top">
                                                                        <div
                                                                            className="gf-activity-avatar"
                                                                            style={{ background: avatarColor(name) }}
                                                                            aria-hidden="true"
                                                                        >
                                                                            {initialsFor(name)}
                                                                            <span className={`gf-activity-avatar-badge${mention ? ' is-mention' : ''}`}>
                                                                                {mention ? '@' : (
                                                                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                                                                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                                                    </svg>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                        <div className="gf-activity-main">
                                                                            <div className="gf-activity-name-row">
                                                                                <div>
                                                                                    <strong>{name}</strong>{' '}
                                                                                    <span className={`gf-activity-verb${mention ? ' is-mention' : ''}`}>
                                                                                        {mention ? 'mentioned you' : 'commented'}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="gf-activity-time">{formatWhen(notification.createdAt)}</span>
                                                                            </div>
                                                                            {(orderNumber || customer) && (
                                                                                <div className="gf-activity-order">
                                                                                    {orderNumber && <span className="gf-activity-order-pill">ORDER #{orderNumber}</span>}
                                                                                    {customer && <span className="gf-activity-customer">{customer}</span>}
                                                                                </div>
                                                                            )}
                                                                            <div className={`gf-activity-bubble${mention ? ' is-mention' : ''}`}>
                                                                                <CommentBody text={comment} />
                                                                            </div>
                                                                            {replyText && (
                                                                                <div className="gf-activity-reply">
                                                                                    <div className="gf-activity-reply-label">You replied · {formatWhen(reply.createdAt)}</div>
                                                                                    <div className="gf-activity-reply-bubble">{replyText}</div>
                                                                                </div>
                                                                            )}
                                                                            <div className="gf-activity-actions">
                                                                                <button type="button" className="is-reply" onClick={() => openChat(notification)}>
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                                                        <path d="M9 14L4 9l5-5" />
                                                                                        <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
                                                                                    </svg>
                                                                                    Reply
                                                                                </button>
                                                                                <button type="button" onClick={() => openChat(notification)}>
                                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                                                                    </svg>
                                                                                    Open chat
                                                                                </button>
                                                                                {orderNumber && (
                                                                                    <button type="button" onClick={() => viewOrder(notification)}>
                                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                                                                            <path d="M14 3h7v7" />
                                                                                            <path d="M10 14L21 3" />
                                                                                            <path d="M21 14v7H3V3h7" />
                                                                                        </svg>
                                                                                        View order
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </article>
                                                            );
                                                        })}
                                                    </section>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>,
                        document.body
                        )}
                        <li className="nav-item gf-theme-switch">
                            <button
                                ref={themeButtonRef}
                                type="button"
                                className="gf-theme-trigger"
                                aria-haspopup="menu"
                                aria-expanded={themeOpen}
                                aria-label="Color theme"
                                title="Color theme"
                                onClick={() => {
                                    const rect = themeButtonRef.current?.getBoundingClientRect();
                                    if (rect) {
                                        const width = 200;
                                        const left = Math.min(
                                            Math.max(12, rect.right - width),
                                            window.innerWidth - width - 12
                                        );
                                        setThemeMenuPos({ top: rect.bottom + 8, left });
                                    }
                                    setThemeOpen((open) => !open);
                                }}
                            >
                                {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
                            </button>
                            {themeOpen && themeMenuPos && createPortal(
                                <div
                                    className="gf-theme-menu"
                                    role="menu"
                                    style={{ top: themeMenuPos.top, left: themeMenuPos.left }}
                                >
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={theme !== 'dark'}
                                        className={`gf-theme-option${theme !== 'dark' ? ' is-active' : ''}`}
                                        onClick={() => { setThemeMode('light'); setThemeOpen(false); }}
                                    >
                                        <SunIcon />
                                        <span>Light Mode</span>
                                        {theme !== 'dark' && <span className="gf-theme-check" aria-hidden="true">✓</span>}
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={theme === 'dark'}
                                        className={`gf-theme-option${theme === 'dark' ? ' is-active' : ''}`}
                                        onClick={() => { setThemeMode('dark'); setThemeOpen(false); }}
                                    >
                                        <MoonIcon />
                                        <span>Dark Mode</span>
                                        {theme === 'dark' && <span className="gf-theme-check" aria-hidden="true">✓</span>}
                                    </button>
                                </div>,
                                document.body
                            )}
                        </li>
                        <li class="nav-item dropdown">
                            <a class="nav-icon dropdown-toggle p-0 py-2" href="#" data-bs-toggle="dropdown">
                                <img src={profile} class="avatar img-fluid rounded" alt="Charles Hall" />
                            </a>
                            <div class="dropdown-menu dropdown-menu-end">
                                <a class="dropdown-item" href="pages-profile"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-user align-middle me-1"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg> Profile</a>
                                {/* <a class="dropdown-item" href="#"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-pie-chart align-middle me-1"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path><path d="M22 12A10 10 0 0 0 12 2v10z"></path></svg> Analytics</a>
                                <div class="dropdown-divider"></div> */}
                                <a class="dropdown-item" href="pages-settings"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-settings align-middle me-1"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg> Settings &amp;
                                    Privacy</a>
                                {/* <a class="dropdown-item" href="#"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-help-circle align-middle me-1"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg> Help Center</a>
                                <div class="dropdown-divider"></div> */}
                                <a class="dropdown-item" onClick={logOut}>Log out</a>
                            </div>
                        </li>
                    </ul>
                   
                </div>
            </nav>
        </>
    )
}

export default TopBar;